import { type ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useSelectedMember } from '@/hooks/use-selected-member';
import { cn } from '@/utils/cn';
import type { PodiumRowData } from '../utils/bonus-indicators';
import { Position, RowSubject, RowMeta } from './bonus-chip-podium-parts';

function useRowClick(row: PodiumRowData): (() => void) | undefined {
  const { isAdmin } = useAuth();
  const { setSelectedId } = useSelectedMember();
  if (!isAdmin || !row.collectorId) return undefined;
  return (): void => setSelectedId(row.collectorId as string);
}

function PodiumRow({ row }: { row: PodiumRowData }): ReactNode {
  const onClick = useRowClick(row);
  const canClick = !!onClick;
  return (
    <div
      className={cn(
        'flex items-center gap-sm rounded-md px-1.5 py-1',
        row.isCurrent && 'bg-brand-red/10',
        canClick && 'cursor-pointer transition-colors hover:bg-white/[0.04]'
      )}
      onClick={onClick}
      role={canClick ? 'button' : undefined}
      tabIndex={canClick ? 0 : undefined}
    >
      <Position pos={row.position} />
      <RowSubject row={row} />
      <RowMeta row={row} />
    </div>
  );
}

export function HeroPodium(p: {
  rows: PodiumRowData[];
  userOffPodium: PodiumRowData | null;
}): ReactNode {
  return (
    <div className="-mx-1 divide-y divide-white/[0.04]">
      {p.rows.map((r) => (
        <PodiumRow key={r.position} row={r} />
      ))}
      {p.userOffPodium && (
        <div className="border-t border-white/[0.06]">
          <PodiumRow row={p.userOffPodium} />
        </div>
      )}
    </div>
  );
}
